<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch, type ComponentPublicInstance } from 'vue'
import { storeToRefs } from 'pinia'
import { useRoute, useRouter } from 'vue-router'

import UiIcon from '@/components/common/UiIcon.vue'
import { localizedPath } from '@/router/i18nPath'
import { DEFAULT_LOCALE, type Locale } from '@/router/locales'
import { useContentStore } from '@/stores/content.store'
import { useFrontpageStore } from '@/stores/frontpage.store'
import { useLocaleStore } from '@/stores/locale.store'

const router = useRouter()
const route = useRoute()
const store = useLocaleStore()
const contentStore = useContentStore()
const frontpageStore = useFrontpageStore()
const { pages, posts, gyms, trainers } = storeToRefs(contentStore)
const { frontpage } = storeToRefs(frontpageStore)

const rootEl = ref<HTMLElement | null>(null)
const btnEl = ref<HTMLButtonElement | null>(null)
const open = ref(false)
const activeIndex = ref(0)
const optionEls = ref<(HTMLButtonElement | null)[]>([])
const menuId = 'language-switcher-menu'
const triggerId = 'language-switcher-trigger'

const options = computed(() =>
  store.availableLocales.map((locale) => ({
    value: locale,
    label: locale === 'no' ? 'Norsk' : 'English',
    shortLabel: locale.toUpperCase(),
  })),
)

const currentIndex = computed(() => {
  const idx = options.value.findIndex((option) => option.value === store.currentLocale)
  return idx >= 0 ? idx : 0
})

const currentOption = computed(() => {
  return options.value[currentIndex.value] ?? {
    value: store.currentLocale,
    label: store.currentLocale === 'no' ? 'Norsk' : 'English',
    shortLabel: String(store.currentLocale).toUpperCase(),
  }
})

const panelLabel = computed(() => (
  store.currentLocale === 'no' ? 'Velg språk' : 'Choose language'
))

const normalizedRouteName = computed(() => {
  if (typeof route.name !== 'string') return ''
  const name = route.name.replace(/-(en|no)$/, '')
  return name === '404' ? 'notFound' : name
})

const currentRouteContent = computed(() => {
  const slug = typeof route.params.slug === 'string' ? route.params.slug : ''

  switch (normalizedRouteName.value) {
    case 'home': {
      const frontpageId = frontpage.value?.frontpage_id
      if (typeof frontpageId !== 'number') return null
      return pages.value.find((page) => page.id === frontpageId) ?? null
    }
    case 'page':
      return pages.value.find((page) => page.slug === slug) ?? null
    case 'gym':
      return gyms.value.find((gym) => gym.slug === slug) ?? null
    case 'trainer':
      return trainers.value.find((trainer) => trainer.slug === slug) ?? null
    case 'campaign':
    case 'post':
      return posts.value.find((post) => post.slug === slug) ?? null
    default:
      return null
  }
})

function resolveTranslatedPath(nextLang: Locale) {
  const translated = currentRouteContent.value?.translations?.[nextLang]
  const relativeUrl = translated?.relativeUrl?.trim()

  if (translated?.exists && relativeUrl) {
    return relativeUrl
  }

  if (normalizedRouteName.value === 'notFound') {
    return localizedPath('home', undefined, nextLang)
  }

  if (normalizedRouteName.value === 'home') {
    return localizedPath(normalizedRouteName.value, undefined, nextLang)
  }

  return nextLang === DEFAULT_LOCALE ? '/' : `/${nextLang}`
}

function setOptionRef(el: Element | ComponentPublicInstance | null, index: number) {
  optionEls.value[index] = el instanceof HTMLButtonElement ? el : null
}

function focusOption(index: number) {
  activeIndex.value = index
  void nextTick(() => {
    optionEls.value[index]?.focus({ preventScroll: true })
  })
}

function openMenu(index?: number) {
  open.value = true
  activeIndex.value = typeof index === 'number' ? index : currentIndex.value

  if (typeof index === 'number') {
    focusOption(index)
  }
}

function close(options: { restoreFocus?: boolean } = {}) {
  open.value = false

  if (options.restoreFocus) {
    void nextTick(() => {
      btnEl.value?.focus({ preventScroll: true })
    })
  }
}

function switchLang(nextLang: Locale) {
  if (nextLang === store.currentLocale) {
    close({ restoreFocus: true })
    return
  }

  close()

  void router.push({
    path: resolveTranslatedPath(nextLang),
    ...(currentRouteContent.value?.translations?.[nextLang]?.exists
      ? {
          query: route.query,
          hash: route.hash,
        }
      : {}),
  })
}

function toggle() {
  if (open.value) {
    close()
    return
  }

  openMenu()
}

function moveActive(delta: number) {
  const total = options.value.length
  if (!total) return

  const nextIndex = (activeIndex.value + delta + total) % total
  focusOption(nextIndex)
}

function focusBoundary(position: 'start' | 'end') {
  if (!options.value.length) return

  focusOption(position === 'start' ? 0 : options.value.length - 1)
}

function onTriggerKeydown(event: KeyboardEvent) {
  if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
    event.preventDefault()
    openMenu(0)
    return
  }

  if (event.key === 'ArrowUp') {
    event.preventDefault()
    openMenu(options.value.length - 1)
  }
}

function onMenuKeydown(event: KeyboardEvent) {
  if (!open.value) return

  if (event.key === 'Escape') {
    event.preventDefault()
    close({ restoreFocus: true })
    return
  }

  if (event.key === 'ArrowDown') {
    event.preventDefault()
    moveActive(1)
    return
  }

  if (event.key === 'ArrowUp') {
    event.preventDefault()
    moveActive(-1)
    return
  }

  if (event.key === 'Home') {
    event.preventDefault()
    focusBoundary('start')
    return
  }

  if (event.key === 'End') {
    event.preventDefault()
    focusBoundary('end')
    return
  }

}

function onFocusOut(event: FocusEvent) {
  const nextTarget = event.relatedTarget

  if (nextTarget instanceof Node && rootEl.value?.contains(nextTarget)) {
    return
  }

  close()
}

function onPointerDown(event: PointerEvent) {
  if (!open.value) return

  const target = event.target
  if (target instanceof Node && rootEl.value?.contains(target)) {
    return
  }

  close()
}

watch(open, (isOpen) => {
  if (isOpen) {
    document.addEventListener('pointerdown', onPointerDown, true)
    return
  }

  document.removeEventListener('pointerdown', onPointerDown, true)
})

watch(() => route.fullPath, () => {
  close()
})

onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', onPointerDown, true)
})
</script>

<template>
  <div
    ref="rootEl"
    class="relative flex items-center"
    @focusout="onFocusOut"
  >
    <button
      :id="triggerId"
      ref="btnEl"
      type="button"
      class="utility-trigger p-2"
      :aria-controls="open ? menuId : undefined"
      :aria-expanded="open ? 'true' : 'false'"
      :aria-label="`${panelLabel}: ${currentOption.label}`"
      @click="toggle"
      @keydown="onTriggerKeydown"
    >
      <UiIcon name="Globe" class="size-6" />
      <span class="sr-only">{{ panelLabel }}</span>
      <span class="inline-flex min-w-5 justify-center whitespace-nowrap text-sm font-semibold text-base-content">
        {{ currentOption.shortLabel }}
      </span>
    </button>

    <div
      v-if="open"
      :id="menuId"
      :aria-label="panelLabel"
      :aria-labelledby="triggerId"
      role="menu"
      aria-orientation="vertical"
      tabindex="-1"
      class="panel-ui absolute right-0 top-full z-10 mt-2 min-w-40 p-1"
      @keydown="onMenuKeydown"
    >
      <button
        v-for="(opt, idx) in options"
        :key="opt.value"
        :ref="(el) => setOptionRef(el, idx)"
        type="button"
        role="menuitem"
        class="panel-option px-3"
        :class="{ 'bg-accent text-accent-foreground': idx === activeIndex }"
        :aria-current="opt.value === store.currentLocale ? 'true' : undefined"
        @mouseenter="activeIndex = idx"
        @focus="activeIndex = idx"
        @click="switchLang(opt.value)"
      >
        <span class="block text-sm font-medium">{{ opt.label }}</span>
      </button>
    </div>
  </div>
</template>
